import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '~/components/ui/alertDialog'

/**
 * A button that asks before it acts (status changes, deletes). Without
 * `trigger`, the caller opens it (`open` / `onOpenChange`), e.g. after a
 * select picked a value, and says where keyboard focus returns once it
 * closes (`returnFocusTo`; with a trigger it returns there, without one and
 * without an element it falls to the page).
 */
export function ConfirmDialog({
  trigger,
  open,
  onOpenChange,
  returnFocusTo,
  title,
  description,
  confirmLabel,
  onConfirm,
  destructive = false,
}: {
  trigger?: ReactNode
  open?: boolean
  onOpenChange?: (open: boolean) => void
  returnFocusTo?: () => HTMLElement | null | undefined
  title: string
  description: string
  confirmLabel: string
  onConfirm: () => void
  destructive?: boolean
}) {
  const { t } = useTranslation()
  return (
    <AlertDialog
      open={open}
      onOpenChange={onOpenChange}
    >
      {trigger !== undefined && (
        <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>
      )}
      <AlertDialogContent
        onCloseAutoFocus={(event) => {
          const target = returnFocusTo?.()
          if (target) {
            event.preventDefault()
            target.focus()
          }
        }}
      >
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t('shell.common.cancel')}</AlertDialogCancel>
          <AlertDialogAction
            variant={destructive ? 'destructive' : 'default'}
            onClick={onConfirm}
          >
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
